import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClimbingRoute } from '../models/climbing/ClimbingRoute';
import { Gym } from '../models/gym/Gym';
import { getGym } from '../services/firebase/gym';
import { getClimbingRoute, archiveClimbingRoute } from '../services/firebase/climbingRoute';
import { removeVideoFromRoute, addVideoToRoute } from '../services/firebase/routeVideos';
import { AvailableColors } from '../models/climbing/RouteColors';
import { GradeSystem, getGradeAsString, ClimbPerformance } from '../models/climbing/Grade';
import { useAuth } from '../contexts/AuthContext';
import { getFilteredClimbs } from '../services/firebase/climbs';
import { ClimbRecord } from '../models/user/Climber';
import { RouteFeedback, RouteAggregatedFeedback } from '../models/climbing/RouteFeedback';
import { RouteFeedbackService } from '../services/firebase/routeFeedbackService';
import { RouteFeedbackForm } from '../components/feedback/RouteFeedbackForm';
import { RouteFeedbackDisplay } from '../components/feedback/RouteFeedbackDisplay';
import { RouteFeedbackList } from '../components/feedback/RouteFeedbackList';
import CloseButton from '../components/CloseButton';
import '../styles/RouteDetail.css';

const RouteDetail: React.FC = () => {
  const { gymId, routeId } = useParams<{ gymId: string; routeId: string }>();
  const [route, setRoute] = useState<ClimbingRoute | null>(null);
  const [gym, setGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userClimbs, setUserClimbs] = useState<ClimbRecord[]>([]);
  const [climbsLoading, setClimbsLoading] = useState(false);
  
  // Feedback states
  const [aggregatedFeedback, setAggregatedFeedback] = useState<RouteAggregatedFeedback | null>(null);
  const [userFeedback, setUserFeedback] = useState<RouteFeedback | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showFeedbackDetails, setShowFeedbackDetails] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  
  // Video states
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);
  
  const navigate = useNavigate();
  const { currentUser, climberProfile, refreshClimberProfile } = useAuth();

  // Helper function to check if user has required role in the specific gym
  const hasRequiredRole = (profile: any, gymId: string | undefined): boolean => {
    if (!profile || !gymId) return false;
    
    const requiredRoles = ['setter', 'owner', 'fre'];
    
    // Check gym-specific role if method exists
    if (typeof profile.getRoleInGym === 'function') {
      const gymRole = profile.getRoleInGym(gymId);
      return requiredRoles.includes(gymRole);
    }
    
    // Check gymMemberships directly if method doesn't exist
    if (profile.gymMemberships && profile.gymMemberships[gymId]) {
      return requiredRoles.includes(profile.gymMemberships[gymId]);
    }
    
    // Fallback to global role if there's no gym-specific role system
    // Only use this if the application doesn't fully implement gym-specific roles yet
    if (profile.role && requiredRoles.includes(profile.role)) {
      return true;
    }
    
    return false;
  };

  useEffect(() => {
    const fetchRouteAndGym = async () => {
      if (!gymId || !routeId) {
        setError('Invalid gym or route ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch gym details
        console.log(`Fetching gym with ID: ${gymId}`);
        const gymData = await getGym(gymId);
        if (!gymData) {
          setError('Gym not found');
          setLoading(false);
          return;
        }
        setGym(gymData);
        
        // Fetch specific climbing route
        console.log(`Fetching route with ID: ${routeId}`);
        const routeData = await getClimbingRoute(gymId, routeId);
        if (!routeData) {
          setError('Route not found');
          setLoading(false);
          return;
        }
        setRoute(routeData);
        
        setError('');
      } catch (err: any) {
        console.error('Error fetching route data:', err);
        setError('Failed to load route data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRouteAndGym();
  }, [gymId, routeId]);

  // Fetch user's climbs for this route
  useEffect(() => {
    const fetchUserClimbs = async () => {
      if (!currentUser || !gymId || !routeId) {
        return;
      }

      try {
        setClimbsLoading(true);
        const climbs = await getFilteredClimbs(
          currentUser.uid,
          { routeId, gymId },
          100 // Get all attempts for this route
        );
        setUserClimbs(climbs);
      } catch (err: any) {
        console.error('Error fetching user climbs:', err);
      } finally {
        setClimbsLoading(false);
      }
    };

    fetchUserClimbs();
  }, [currentUser, gymId, routeId]);

  // Fetch feedback data
  useEffect(() => {
    const fetchFeedbackData = async () => {
      if (!routeId || !gymId || !gym?.feedbackSettings?.enabled) {
        return;
      }

      try {
        setFeedbackLoading(true);
        
        // Load aggregated feedback
        const aggregated = await RouteFeedbackService.getRouteAggregatedFeedback(gymId, routeId);
        setAggregatedFeedback(aggregated);
        
        // Load user's feedback if logged in
        if (currentUser) {
          const userFeedbackData = await RouteFeedbackService.getClimberFeedbackForRoute(gymId, routeId, currentUser.uid);
          setUserFeedback(userFeedbackData);
        }
      } catch (err) {
        console.error('Error fetching feedback data:', err);
      } finally {
        setFeedbackLoading(false);
      }
    };

    fetchFeedbackData();
  }, [routeId, gymId, currentUser, gym?.feedbackSettings?.enabled]);

  const getColorName = (colorValue?: string): string => {
    if (!colorValue) return 'Unknown';
    const color = AvailableColors.find(c => c.value === colorValue);
    return color ? color.name : 'Unknown';
  };

  const getColorHex = (colorValue?: string): string => {
    if (!colorValue) return '#cccccc'; // Default gray
    const color = AvailableColors.find(c => c.value === colorValue);
    return color ? color.hexCode : '#cccccc';
  };

  const getTextColorForBackground = (colorValue?: string): string => {
    if (!colorValue) return '#ffffff'; // Default to white text
    
    // If it's a dark color, use white text, otherwise use black text
    const isDark = ['black', 'blue', 'green', 'purple', 'red', 'brown', 'grey'].includes(colorValue);
    return isDark ? '#ffffff' : '#000000';
  };

  const formatDate = (timestamp?: number): string => {
    if (!timestamp) return 'Unknown date';
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPerformanceLabel = (performance: ClimbPerformance): string => {
    switch (performance) {
      case ClimbPerformance.ON_SIGHT:
        return 'On Sight';
      case ClimbPerformance.FLASH:
        return 'Flash';
      case ClimbPerformance.REDPOINT:
        return 'Redpoint';
      case ClimbPerformance.TOPPED:
        return 'Topped';
      case ClimbPerformance.NOT_TOPPED:
        return 'Attempted';
      case ClimbPerformance.BLOCKS:
        return 'Blocks';
      default:
        return 'Unknown';
    }
  };

  const getPerformanceClass = (performance: ClimbPerformance): string => {
    switch (performance) {
      case ClimbPerformance.ON_SIGHT:
        return 'on-sight';
      case ClimbPerformance.FLASH:
        return 'flash';
      case ClimbPerformance.REDPOINT:
        return 'redpoint';
      case ClimbPerformance.TOPPED:
        return 'topped';
      case ClimbPerformance.NOT_TOPPED:
        return 'attempted';
      case ClimbPerformance.BLOCKS:
        return 'blocks';
      default:
        return 'unknown';
    }
  };

  const handleLogClimb = () => {
    if (!currentUser) {
      // Redirect to login if not logged in
      navigate('/login', { state: { from: `/gyms/${gymId}/routes/${routeId}` } });
      return;
    }
    navigate(`/gyms/${gymId}/routes/${routeId}/log`);
  };

  // Feedback handlers
  const handleFeedbackSubmitted = async () => {
    setShowFeedbackForm(false);
    
    if (!routeId || !gymId) return;
    
    try {
      // Refresh feedback data
      const aggregated = await RouteFeedbackService.getRouteAggregatedFeedback(gymId, routeId);
      setAggregatedFeedback(aggregated);
      
      if (currentUser) {
        const userFeedbackData = await RouteFeedbackService.getClimberFeedbackForRoute(gymId, routeId, currentUser.uid);
        setUserFeedback(userFeedbackData);
      }
    } catch (err) {
      console.error('Error refreshing feedback data:', err);
    }
  };

  const handleDeleteFeedback = async () => {
    if (!userFeedback || !gymId) return;
    
    if (!window.confirm('Are you sure you want to delete your feedback?')) return;
    
    try {
      await RouteFeedbackService.deleteFeedback(gymId, userFeedback.id);
      setUserFeedback(null);
      
      // Refresh aggregated feedback
      if (routeId) {
        const aggregated = await RouteFeedbackService.getRouteAggregatedFeedback(gymId, routeId);
        setAggregatedFeedback(aggregated);
      }
    } catch (err) {
      console.error('Error deleting feedback:', err);
    }
  };

  const handleArchiveRoute = async () => {
    if (!gymId || !routeId) return;
    
    if (window.confirm('Are you sure you want to archive this route? It will be removed from the gym but stored in the archive.')) {
      try {
        setLoading(true);
        await archiveClimbingRoute(gymId, routeId);
        navigate(`/gyms/${gymId}`);
      } catch (err: any) {
        console.error('Error archiving route:', err);
        setError('Failed to archive route. Please try again.');
        setLoading(false);
      }
    }
  };

  const getYouTubeEmbedUrl = (url: string): string => {
    try {
      // Extract video ID from various YouTube URL formats
      const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([A-Za-z0-9_-]{11})/);
      if (videoIdMatch && videoIdMatch[1]) {
        return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
      }
      return url; // Fallback to original URL
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return url;
    }
  };

  const handleRemoveVideo = async (videoId: string) => {
    if (!currentUser || !gymId || !routeId) return;

    if (!window.confirm('Are you sure you want to remove this video?')) return;

    try {
      await removeVideoFromRoute(gymId, routeId, videoId, currentUser.uid);
      
      // Refresh the route data to update the videos list
      const updatedRoute = await getClimbingRoute(gymId, routeId);
      if (updatedRoute) {
        setRoute(updatedRoute);
      }
    } catch (err: any) {
      console.error('Error removing video:', err);
      setError(err.message || 'Failed to remove video');
    }
  };

  const handleAddVideo = async () => {
    if (!currentUser || !gymId || !routeId || !videoUrl.trim()) return;

    setAddingVideo(true);
    try {
      await addVideoToRoute(gymId, routeId, {
        url: videoUrl.trim(),
        title: videoTitle.trim() || undefined,
        uploadedBy: currentUser.uid,
        uploadedByName: climberProfile?.name || climberProfile?.username || currentUser.email || 'Anonymous'
      });

      // Refresh the route data to update the videos list
      const updatedRoute = await getClimbingRoute(gymId, routeId);
      if (updatedRoute) {
        setRoute(updatedRoute);
      }

      // Reset form and close modal
      setVideoUrl('');
      setVideoTitle('');
      setShowAddVideoModal(false);
    } catch (err: any) {
      console.error('Error adding video:', err);
      setError(err.message || 'Failed to add video');
    } finally {
      setAddingVideo(false);
    }
  };

  if (loading) {
    return (
      <div className="route-detail-container">
        <div className="loading-indicator">Loading route data...</div>
      </div>
    );
  }

  if (error || !route || !gym) {
    return (
      <div className="route-detail-container">
        <CloseButton returnTo={`/gyms/${gymId}`} />
        <div className="route-detail-header">
          <h1>Route Details</h1>
        </div>
        <div className="error-message">{error || 'Route not found'}</div>
      </div>
    );
  }

  return (
    <div className="route-detail-container">
      <CloseButton returnTo={`/gyms/${gymId}`} />
      <div className="route-detail-header">
        <h1>Route Details</h1>
      </div>

      <div className="route-info-card">
        <div className="route-primary-display">
          <div className="grade-circle-small" style={{ 
            backgroundColor: getColorHex(route.color), 
            color: getTextColorForBackground(route.color),
            borderColor: getColorHex(route.color) === '#ffffff' ? '#dddddd' : 'transparent'
          }}>
            {route.grade !== undefined 
              ? getGradeAsString(route.grade, GradeSystem.FRENCH) 
              : 'N/A'}
          </div>
          
          <div className="route-info-grid">
            <div className="info-row">
              {route.location && (
                <div className="info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{route.location}</span>
                </div>
              )}
              {route.setterName && (
                <div className="info-item">
                  <span className="info-label">Setter</span>
                  <span className="info-value">{route.setterName}</span>
                </div>
              )}
            </div>
            
            <div className="info-row">
              {route.date && (
                <div className="info-item">
                  <span className="info-label">Set on</span>
                  <span className="info-value">{formatDate(route.date)}</span>
                </div>
              )}
              {route.points !== undefined && (
                <div className="info-item">
                  <span className="info-label">Bananas</span>
                  <span className="info-value">{route.points}</span>
                </div>
              )}
            </div>
            
            {route.rope && (
              <div className="info-row">
                <div className="info-item">
                  <span className="info-label">Rope</span>
                  <span className="info-value">{route.rope}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit and Archive buttons for authorized users */}
        {hasRequiredRole(climberProfile, gymId) && (
          <div className="route-actions-compact">
            <button
              className="action-button edit"
              onClick={() => navigate(`/gyms/${gymId}/routes/${routeId}/edit`)}
              aria-label="Edit route"
              title="Edit route"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Edit
            </button>
            <button
              className="action-button add-video"
              onClick={() => setShowAddVideoModal(true)}
              aria-label="Add video"
              title="Add video"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 7l-7 5 7 5V7z"></path>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              Add Video
            </button>
            <button
              className="action-button archive"
              onClick={handleArchiveRoute}
              aria-label="Archive route"
              title="Archive route"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8"></polyline>
                <rect x="1" y="3" width="22" height="5"></rect>
                <line x1="10" y1="12" x2="14" y2="12"></line>
              </svg>
              Archive
            </button>
          </div>
        )}

        {/* Description section if present */}
        {route.comments && (
          <div className="route-description">
            <span className="description-label">Description</span>
            <p className="description-text">{route.comments}</p>
          </div>
        )}

        {/* Archived status indicator */}
        {route.status === 'archived' && (
          <div className="archived-indicator">
            <span>📁 Archived Route</span>
          </div>
        )}
      </div>

      {/* Route Feedback Section */}
      {gym?.feedbackSettings?.enabled && routeId && (
        <div className="route-feedback-section">
          <div className="feedback-header">
            <h3>Route Feedback</h3>
            
            {currentUser && (
              <div className="feedback-actions">
                {userFeedback ? (
                  <>
                    <button 
                      className="feedback-action-btn edit"
                      onClick={() => setShowFeedbackForm(true)}
                    >
                      Edit Feedback
                    </button>
                    <button 
                      className="feedback-action-btn delete"
                      onClick={handleDeleteFeedback}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <button 
                    className="feedback-action-btn add"
                    onClick={() => setShowFeedbackForm(true)}
                  >
                    Add Feedback
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Aggregated Feedback Display */}
          {aggregatedFeedback && !feedbackLoading && (
            <RouteFeedbackDisplay
              aggregatedFeedback={aggregatedFeedback}
              showDetails={showFeedbackDetails}
            />
          )}

          {/* Toggle for showing detailed feedback */}
          {aggregatedFeedback && (
            aggregatedFeedback.totalStarsCount > 0 || 
            aggregatedFeedback.suggestedGradeCount > 0 || 
            aggregatedFeedback.totalCommentsCount > 0
          ) && (
            <button 
              className="toggle-details-btn"
              onClick={() => setShowFeedbackDetails(!showFeedbackDetails)}
            >
              {showFeedbackDetails ? 'Hide Details' : 'Show Details'}
            </button>
          )}

          {/* Feedback Form */}
          {showFeedbackForm && currentUser && gym.feedbackSettings && (
            <RouteFeedbackForm
              routeId={routeId}
              gymId={gymId!}
              feedbackSettings={gym.feedbackSettings}
              existingFeedback={userFeedback}
              onSubmitted={handleFeedbackSubmitted}
              onCancel={() => setShowFeedbackForm(false)}
            />
          )}

          {/* Individual Feedback Comments */}
          {showFeedbackDetails && gym.feedbackSettings?.allowComments && gymId && (
            <div className="feedback-comments-section">
              <h4>Comments</h4>
              <RouteFeedbackList
                routeId={routeId}
                gymId={gymId}
                showComments={true}
                showStars={false}
                showGrades={false}
                maxItems={10}
              />
            </div>
          )}

          {feedbackLoading && (
            <div className="feedback-loading">
              Loading feedback...
            </div>
          )}
        </div>
      )}

      {/* Route Videos Section */}
      {route.videos && route.videos.length > 0 && (
        <div className="route-videos-section">
          <h3>Videos</h3>
          <div className="videos-grid">
            {route.videos.map((video: any) => (
              <div key={video.id} className="video-item">
                <div className="video-wrapper">
                  <iframe
                    src={getYouTubeEmbedUrl(video.url)}
                    title={video.title || 'Route Video'}
                    frameBorder="0"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                </div>
                <div className="video-info">
                  {video.title && (
                    <div className="video-title">{video.title}</div>
                  )}
                  <div className="video-meta">
                    <span className="video-uploader">
                      Shared by {video.uploadedByName || 'Anonymous'}
                    </span>
                    <span className="video-date">
                      {formatDate(video.uploadedAt)}
                    </span>
                  </div>
                  <div className="video-actions">
                    <button 
                      className="btn btn-secondary btn-small"
                      onClick={() => window.open(video.url, '_blank')}
                    >
                      Open in YouTube
                    </button>
                    {currentUser?.uid === video.uploadedBy && (
                      <button 
                        className="btn btn-danger btn-small"
                        onClick={() => handleRemoveVideo(video.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User's climb history for this route */}
      {currentUser && userClimbs.length > 0 && (
        <div className="climb-history-section">
          <h3>Your Climbs on This Route</h3>
          {climbsLoading ? (
            <div className="loading-indicator">Loading your climbs...</div>
          ) : (
            <div className="climb-history-list">
              {userClimbs.map((climb, index) => (
                <div 
                  key={index} 
                  className="climb-history-item"
                >
                  <div className="climb-history-header">
                    <div className={`performance-badge ${getPerformanceClass(climb.performance)}`}>
                      {getPerformanceLabel(climb.performance)}
                    </div>
                    <div className="climb-actions">
                      <div className="climb-date">
                        {formatDate(climb.date)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="climb-history-details">
                    {climb.attempt !== undefined && (
                      <div className="climb-detail">
                        <span className="label">Attempt:</span>
                        <span className="value">#{climb.attempt}</span>
                      </div>
                    )}
                    
                    {climb.hangs !== undefined && climb.hangs > 0 && (
                      <div className="climb-detail">
                        <span className="label">Hangs:</span>
                        <span className="value">{climb.hangs}</span>
                      </div>
                    )}
                    
                    {climb.reachedTop !== undefined && (
                      <div className="climb-detail">
                        <span className="label">Reached Top:</span>
                        <span className="value">{climb.reachedTop ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                  </div>
                  
                  {climb.notes && (
                    <div className="climb-notes">
                      <span className="label">Notes:</span>
                      <span className="value">{climb.notes}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Show message if user is logged in but has no climbs */}
      {currentUser && userClimbs.length === 0 && !climbsLoading && (
        <div className="no-climbs-section">
          <p>You haven't climbed this route yet. Log your first attempt!</p>
        </div>
      )}

      {/* Route picture */}
      {route.picture && (
        <div className="route-picture-container">
          <img 
            src={route.picture} 
            alt="Route" 
            className="route-picture"
            onError={(e) => {
              e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
              e.currentTarget.alt = 'Image not available';
            }}
          />
        </div>
      )}

      {/* Floating action button for logging climbs */}
      <button 
        className="log-climb-button"
        onClick={handleLogClimb}
        aria-label="Log climb"
        title="Log climb"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19V5" />
          <path d="M5 12h14" />
        </svg>
      </button>

      {/* Add Video Modal */}
      {showAddVideoModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Video to Route</h3>
              <CloseButton onClose={() => setShowAddVideoModal(false)} />
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="video-url">YouTube URL *</label>
                <input
                  id="video-url"
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="video-title">Title (optional)</label>
                <input
                  id="video-title"
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Enter a title for this video"
                  className="form-control"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddVideoModal(false)}
                disabled={addingVideo}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddVideo}
                disabled={addingVideo || !videoUrl.trim()}
              >
                {addingVideo ? 'Adding...' : 'Add Video'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetail;
